/**
 * useCurrencyFormat Hook
 *
 * Provides currency formatting and symbol functions.
 * PRIMARY source of truth: Organization Settings (`organization_settings.ui_settings.Currency`)
 * via the central `useOrgSettings` hook.
 * FALLBACK: `finance_settings.default_currency` (legacy).
 *
 * Usage: const { formatCurrency, getCurrencySymbol, baseCurrency } = useCurrencyFormat();
 */

import { useMemo } from 'react';
import { useOrgSettingsCtx } from '@/contexts/OrgSettingsContext';

export function useCurrencyFormat() {
  // Prefer the context value: it lets a parallel app entry (the public
  // storefront) inject a synthetic settings value without hitting the
  // `organization_settings` table as `anon`. Inside the BMS, the
  // `OrgSettingsProvider` mounted in `App.tsx` supplies the same shape.
  const {
    currencyCode,
    currencySymbol,
    numberLocale,
    fmtCurrency: orgFmtCurrency,
  } = useOrgSettingsCtx();

  // baseCurrency exposed for consumers that need the raw ISO code
  const baseCurrency = currencyCode;

  // Memoized format function — delegates to org settings formatter
  const formatCurrency = useMemo(() => {
    return (amount: number | undefined | null) => {
      if (amount === null || amount === undefined) return '-';
      return orgFmtCurrency(amount);
    };
  }, [orgFmtCurrency]);

  // Memoized symbol function
  const getCurrencySymbol = useMemo(() => {
    return () => currencySymbol;
  }, [currencySymbol]);

  // Get currency locale
  const getCurrencyLocale = useMemo(() => {
    return () => numberLocale;
  }, [numberLocale]);

  return {
    formatCurrency,
    getCurrencySymbol,
    getCurrencyLocale,
    baseCurrency
  };
}
